import { commandPaths, type AlertDto, type ChangeSummary, type Cursor, type EventDto, type HealthDto } from "@nabla/api";
import type { PersonDto, SessionView } from "@nabla/api";
import { createAlerts } from "./alerts.ts";
import { createChanges, type HeldChange } from "./changes.ts";
import { createClock } from "./clock.ts";
import { createCommands, type Commands } from "./commands.ts";
import { createConnection } from "./connection.ts";
import type { DurableEvent } from "./cursor.ts";
import { createFeed } from "./feed.ts";
import { createHealth } from "./health.ts";
import { sendJson, type Fetch, type Reach, type Seen } from "./http.ts";
import { createHub, type Hub } from "./hub.ts";
import { changesOf, objectsOf } from "./interest.ts";
import { createPlaces } from "./places.ts";
import { createPrefs, type KeyValue, type Prefs } from "./prefs.ts";
import { createReloadOffer } from "./reload.ts";
import { createSession, type SessionState } from "./session.ts";
import type { Connection } from "./stream.ts";
import { createThreads, type Threads } from "./threads.ts";
import { createViews, type Views } from "./views.ts";

interface ClientDeps {
  readonly fetch: Fetch;
  readonly apiVersion: string;
  readonly now: () => number;
  readonly after: (ms: number, run: () => void) => () => void;
  readonly schedule: (flush: () => void) => void;
  readonly randomKey: () => string;
  readonly storage?: KeyValue | undefined;
}

export interface Core extends Pick<Threads, "putThread" | "saw"> {
  readonly places: Pick<ReturnType<typeof createPlaces>, "reread">;
  readonly hub: Hub;
  readonly views: Views;
  readonly commands: Commands;
  readonly prefs: Prefs;
  readonly deps: ClientDeps;
  now(): number;
  me(): PersonDto["id"] | undefined;
  putChange(change: ChangeSummary): void;
  putPerson(person: PersonDto): void;
  signedOut(reason: "terminou" | "saiu"): void;
  signedIn(view: SessionView): void;
  seen(info: Seen): void;
}

interface ErrorReport {
  readonly message: string;
  readonly stack?: string;
  readonly view?: string;
  readonly requestId?: string;
}

export interface Client extends Core, Threads {
  start(): void;
  stop(): void;
  flush(): void;
  session(): SessionState;
  connection(): Connection;
  offline(): boolean;
  health(): HealthDto | undefined;
  change(id: string): HeldChange | undefined;
  alerts(): readonly AlertDto[];
  reloadOffered(): boolean;
  applied(): Cursor | null;
  reportError(report: ErrorReport): void;
}

interface Routed {
  readonly hub: Hub;
  readonly clock: ReturnType<typeof createClock>;
  readonly reload: ReturnType<typeof createReloadOffer>;
  readonly session: ReturnType<typeof createSession>;
  readonly threads: ReturnType<typeof createThreads>;
  readonly places: ReturnType<typeof createPlaces>;
  readonly changes: ReturnType<typeof createChanges>;
  readonly alerts: ReturnType<typeof createAlerts>;
  readonly health: ReturnType<typeof createHealth>;
  readonly views: Views;
  readonly commands: Commands;
  readonly feed: ReturnType<typeof createFeed>;
}

function createRouting({ hub, clock, reload, session, threads, places, changes, alerts, health, views, commands, feed }: Routed) {
  const applyDurable = (event: DurableEvent): void => {
    if (!feed.advance(event)) return;
    for (const change of changesOf(event)) changes.put(change);
    if (event.type === "conversa") threads.members.putThread(event.thread);
    if (event.type === "pessoas" && event.person) session.members.putPerson(event.person);
    if (event.type === "agenda") health.put(event.health);
    if (event.type === "alerta") alerts.put(event.alert);
    commands.eventApplied(event);
    if (event.type === "dados") views.allStale();
    else if (event.type === "atualizacao") views.invalidate("todos", ["a"]);
    else views.eventApplied(event, objectsOf(event));
  };
  return {
    hear(event: EventDto): void {
      switch (event.type) {
        case "ola":
          return clock.heard(event.serverTime);
        case "conversa-parcial":
        case "conversa-estado":
          return threads.heard(event);
        case "lugares":
          return places.reread();
        case "sessao-encerrada":
          return session.members.signedOut("terminou");
        case "recarregar":
          return event.reason === "versao" ? reload.offer() : void feed.catchUp();
        default:
          return applyDurable(event);
      }
    },
    forget(again: boolean): void {
      feed.forget();
      for (const topic of [...changes.topics(), ...threads.topics(), "alerts"]) hub.touch(topic);
      changes.clear();
      threads.forget();
      places.forget();
      alerts.clear(() => health.put(undefined));
      views.forget(again);
      commands.forget();
    },
  };
}

export function createClient(deps: ClientDeps): Client {
  const { after } = deps;
  const hub = createHub(deps.schedule);
  const clock = createClock(deps.now);
  const prefs = createPrefs(deps.storage);
  const touch = (topic: string) => hub.touch(topic);
  const reload = createReloadOffer(touch);
  const host: Reach = { fetch: deps.fetch, api: deps.apiVersion, seen: reload.seen };
  const reportError = (report: ErrorReport) => void sendJson(host, commandPaths.erroDoNavegador.route, {}, report, null);
  const session = createSession({
    host, touch, heard: clock.heard,
    signedIn: () => threads.signedIn(),
    forget: (again) => routing.forget(again),
  });
  const { generation } = session;
  const signedOut = () => session.members.signedOut("terminou");
  const threads = createThreads({ touch, listed: () => places.listed() });
  const changes = createChanges(deps.now, touch);
  const alerts = createAlerts(touch);
  const health = createHealth({ now: clock.now, after, changed: () => touch("health") });
  const views = createViews({
    host, hub, after, generation, signedOut, now: clock.now, health: health.put,
    applied: () => feed.applied(),
    firstSnapshot: (cursor) => feed.firstSnapshot(cursor),
  });
  const commands = createCommands({ host, hub, after, generation, signedOut, now: deps.now, randomKey: deps.randomKey });
  const backOnline = () => commands.announce("conectado");
  const places = createPlaces({ views, unheard: threads.unheard });
  const connection = createConnection({ now: clock.now, after, opened: places.opened, backOnline, changed: () => touch("connection") });
  const feed = createFeed({
    host, after, generation, signedOut, now: clock.now, reloadAll: views.reloadAll, onConnection: connection.set,
    onEvent: (event) => routing.hear(event),
    onUnreadable(reason) {
      reportError({ message: `evento ilegível: ${reason}`.slice(0, 500) });
      views.allStale();
    },
  });
  const routing = createRouting({ hub, clock, reload, session, threads, places, changes, alerts, health, views, commands, feed });
  return {
    ...threads.members,
    ...session.members,
    ...connection.members,
    hub, views, commands, prefs, deps, places,
    now: clock.now,
    putChange: changes.put,
    seen: reload.seen,
    start: () => void session.load(),
    stop() {
      feed.stop();
      commands.flush();
    },
    flush: () => commands.flush(),
    health: health.get,
    change: changes.get,
    alerts: alerts.list,
    reloadOffered: reload.offered,
    applied: feed.applied,
    reportError,
  };
}
